import { Tooltip } from "antd";
import classNames from "classnames";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import React, { useEffect, useState } from "react";
import LoaderAnimated from "../dashboard/LoaderAnimated";
import SidebarSkeletonLoader from "./SidebarSkeletonLoader";

interface SidebarButtonProps {
  icon?: React.ReactNode;
  children?: React.ReactNode | React.ReactNode[];
  onClick?: () => void;
  contextButton?: React.ReactNode;
  isSelected?: boolean;
  loading?: boolean;
  additionalActionOnClick?: () => void;
  isAdditionalActionLoading?: boolean;
  expand?: any[];
  isExpandLoading?: boolean;
  disabled?: boolean;
  tooltip?: string | React.ReactNode;
  tooltipOffset?: [string | number, string | number];
  expandLimit?: number;
  expandShowAllOnClick?: any;

  isDefaultExpanded?: boolean;
  afterOpenExpandCallback?: any;
  afterCloseExpandCallback?: any;

  subtitle?: string;
}
const SidebarButton = (props: SidebarButtonProps) => {
  const {
    icon,
    children,
    onClick,
    contextButton,
    isSelected,
    loading,
    additionalActionOnClick,
    isAdditionalActionLoading,
    expand,
    isExpandLoading,
    disabled,
    tooltip,
    tooltipOffset,
    expandLimit = 5,
    expandShowAllOnClick,
    isDefaultExpanded,
    afterOpenExpandCallback,
    afterCloseExpandCallback,
    subtitle,
  } = props;
  const hasSelectedExpand = !!expand
    ? expand.some((item) => item.isSelected)
    : false;
  const [isExpand, setIsExpand] = useState(
    hasSelectedExpand || isDefaultExpanded
  );

  useEffect(() => {
    if (hasSelectedExpand) {
      setIsExpand(true);
    }
  }, [hasSelectedExpand]);

  let expandIcon = !isExpand ? (
    <ChevronRight size={16} />
  ) : (
    <ChevronDown size={16} />
  );

  return (
    <div className="nav-sidebar__button-wrapper">
      <button
        className={classNames("nav-sidebar__button", {
          "has-context-button": !!contextButton,
          "is-selected": isSelected,
          "is-loading": loading || isAdditionalActionLoading,
          "has-expand": !!expand,
          "is-disabled": disabled,
          "has-subtitle": !!subtitle,
        })}
        onClick={onClick}
        disabled={loading || isAdditionalActionLoading || disabled}
      >
        <Tooltip
          title={tooltip ? tooltip : null}
          placement="right"
          mouseLeaveDelay={0}
          mouseEnterDelay={0.5}
          // align={{ offset: tooltipOffset ? tooltipOffset : [0, 0] }}
          transitionName=""
          overlayClassName="nav-sidebar__button-tooltip"
        >
          <span className="nav-sidebar__button-inner">
            {loading && (
              <span className="nav-sidebar__button-icon">
                <LoaderAnimated />
              </span>
            )}
            {!loading && !!icon && (
              <span className="nav-sidebar__button-icon">{icon}</span>
            )}
            {!loading && !!expand && (
              <span
                className="nav-sidebar__button-icon is-expand-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpand(!isExpand);
                  if (!!afterCloseExpandCallback && isExpand) {
                    afterCloseExpandCallback();
                  }
                  if (!!afterOpenExpandCallback && !isExpand) {
                    afterOpenExpandCallback();
                  }
                }}
              >
                {expandIcon}
              </span>
            )}
            <span className="nav-sidebar__button-text">{children}</span>
            {!!subtitle && (
              <span className="nav-sidebar__button-subtitle">{subtitle}</span>
            )}
            {!!additionalActionOnClick && !isAdditionalActionLoading && (
              <span
                className={classNames(
                  "nav-sidebar__button-context-button is-additional-action",
                  {
                    "is-loading": isAdditionalActionLoading,
                  }
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  additionalActionOnClick();
                }}
              >
                <Plus size={16} />
              </span>
            )}
            {!!additionalActionOnClick && isAdditionalActionLoading && (
              <span
                className={classNames(
                  "nav-sidebar__button-context-button is-additional-action",
                  {
                    "is-loading": isAdditionalActionLoading,
                  }
                )}
              >
                <LoaderAnimated />
              </span>
            )}
          </span>
        </Tooltip>
        {contextButton}
      </button>
      {!!expand && isExpand && (
        <div className="nav-sidebar__button-expand">
          {isExpandLoading && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                margin: "8px 0 8px 8px",
              }}
            >
              <SidebarSkeletonLoader width="100px" />
              <SidebarSkeletonLoader width="60px" />
              <SidebarSkeletonLoader width="80px" />
            </div>
          )}
          {!isExpandLoading &&
            expand.slice(0, expandLimit).map((item, index) => {
              return (
                <Tooltip
                  key={index}
                  title={
                    <div className="nav-sidebar__button-tooltip-content">
                      <div className="nav-sidebar__button-tooltip-title">
                        {item.tooltip}
                      </div>
                    </div>
                  }
                  placement="right"
                  mouseLeaveDelay={0}
                  mouseEnterDelay={0.5}
                  transitionName=""
                  overlayClassName="nav-sidebar__button-tooltip"
                >
                  <div
                    className={classNames("nav-sidebar__button-expand-item", {
                      "is-selected": item.isSelected,
                    })}
                    onClick={() => {
                      item.onClick();
                    }}
                  >
                    {item.title}
                  </div>
                </Tooltip>
              );
            })}
          {!isExpandLoading &&
            expand.length > expandLimit &&
            !!expandShowAllOnClick && (
              <SidebarButton onClick={expandShowAllOnClick}>
                ... more
              </SidebarButton>
            )}
        </div>
      )}
    </div>
  );
};

export default SidebarButton;
